// Shared bulk tag picker for the Leads + Contacts bulk-action bars.
//
// One component so the two surfaces can't drift — especially on the EMPTY
// state.  The firm has zero tags today, so "no tags exist yet" is the state
// every user will actually hit first; rendering an empty <select> there would
// read as a broken feature rather than an unused one.
//
//   <BulkTagPicker count={n} noun="lead" busy={bool}
//                  onApply={(tag, mode) => Promise} onCancel={fn} />
//
// onApply receives the whole tag row (not just its id) so the caller can put
// the tag's NAME in its confirm + toast copy without re-looking it up.
//
// The parent owns the request + confirm + toast; this is presentation plus
// the tag-catalog fetch.

import { useState } from 'react'
import { Link } from 'react-router-dom'
import useTagCatalog from '../hooks/useTagCatalog'

export default function BulkTagPicker ({ count = 0, noun = 'record', busy = false, onApply, onCancel }) {
  // `true` — this component only mounts while the picker is open, so the
  // catalog should load immediately rather than waiting on another signal.
  const { tags, loading, loaded } = useTagCatalog(true)
  const [tagId, setTagId] = useState('')

  const plural = count === 1 ? noun : `${noun}s`
  const selected = tags.find(t => String(t.id) === String(tagId))
  const canApply = !!selected && !busy

  const apply = (mode) => {
    if (!canApply) return
    onApply?.(selected, mode)
  }

  return (
    <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-sm p-3">
      {loading && !loaded ? (
        <span className="text-xs text-gray-500">Loading tags…</span>
      ) : tags.length === 0 ? (
        // Honest empty state — say what's missing and where to fix it.
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-600">
            No tags exist yet, so there's nothing to apply.
          </span>
          <Link to="/tags" className="text-xs text-indigo-600 hover:text-indigo-800 underline">
            Create a tag →
          </Link>
          <button type="button" onClick={onCancel}
            className="text-xs text-gray-500 hover:text-gray-800 ml-auto">Close</button>
        </div>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500">
            Tag {count} {plural}:
          </span>
          <select
            autoFocus
            value={tagId}
            disabled={busy}
            onChange={e => setTagId(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm"
          >
            <option value="" disabled>Pick a tag…</option>
            {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {selected && (
            <span
              className="inline-block w-3 h-3 rounded-full border border-gray-300"
              style={{ backgroundColor: selected.color || '#6366f1' }}
              aria-hidden="true"
            />
          )}
          <button type="button" onClick={() => apply('attach')} disabled={!canApply}
            className="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed">
            Add tag
          </button>
          <button type="button" onClick={() => apply('detach')} disabled={!canApply}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed">
            Remove tag
          </button>
          <button type="button" onClick={onCancel}
            className="text-xs text-gray-500 hover:text-gray-800">Cancel</button>
        </div>
      )}
    </div>
  )
}
